import { useContext, useMemo, useState } from 'react';
import {
	Button,
	Col,
	Form,
	Input,
	message,
	Row,
	Select,
} from 'antd';
import { IoIosArrowDown } from 'react-icons/io';

import Context from '../../../context/Context';
import { isValidNric } from '../../../utils/utils';

import {
	checkExistingUsernameApi,
	submitModifyNricApi,
} from '../../../api/userOnboarding';

import styles from './modifyUserDetails.module.scss';

const ModifyNric = () => {
	const { SSPFacilities } = useContext(Context);

	const [form] = Form.useForm();

	const userDetails = JSON.parse(
		localStorage.getItem('user-onboard-data')
	);

	const loggedInFullName =
		localStorage.getItem('fullName');

	const isAdmin =
		userDetails?.isAdmin === 'Y';

	const [submit, setSubmit] = useState({
		loading: false,
		success: null,
		error: null,
	});

	/*
	 * =========================================================
	 * NH NAMES BELONGING TO LOGGED-IN USER
	 * =========================================================
	 *
	 * Example:
	 *
	 * "Lions"
	 *
	 * or
	 *
	 * "Lions, Ren Ci"
	 */
	const userNhNames = useMemo(() => {
		return userDetails?.nhName
			? userDetails.nhName
					.split(',')
					.map((item) =>
						item.trim()
					)
					.filter(Boolean)
			: [];
	}, [userDetails?.nhName]);

	/*
	 * =========================================================
	 * ALL NH NAMES FOR ADMIN
	 * =========================================================
	 *
	 * SSPFacilities can contain several facilities
	 * under the same NH, so remove duplicates.
	 */
	const allNhNames = useMemo(() => {
		if (!SSPFacilities?.data) {
			return [];
		}

		return [
			...new Set(
				SSPFacilities.data
					.map((item) =>
						item?.nhName?.trim()
					)
					.filter(Boolean)
			),
		].sort((a, b) =>
			a.localeCompare(b)
		);
	}, [SSPFacilities?.data]);

	/*
	 * =========================================================
	 * NORMAL USER NH
	 * =========================================================
	 *
	 * Requirement:
	 *
	 * Normal user NH must be read-only.
	 *
	 * Since this screen requires one NH value,
	 * use the user's first assigned NH.
	 *
	 * If your normal users can genuinely belong to
	 * multiple NHs and must choose between them,
	 * we can change this later.
	 */
	const normalUserNhName =
		userNhNames.length > 0
			? userNhNames[0]
			: '';

	/*
	 * =========================================================
	 * INITIAL FORM VALUES
	 * =========================================================
	 */
	const initialValues = {
		nhName: !isAdmin
			? normalUserNhName
			: null,
	};

	/*
	 * =========================================================
	 * ADMIN NH CHANGE
	 * =========================================================
	 *
	 * When Admin changes NH, clear user-specific fields.
	 */
	const handleNhChange = () => {
		form.setFieldsValue({
			userName: '',
			fullName: '',
			oldNric: '',
			newNric: '',
		});

		form.setFields([
			{
				name: 'userName',
				errors: [],
			},
		]);
	};

	/*
	 * =========================================================
	 * USERNAME VALIDATION
	 * =========================================================
	 *
	 * When username loses focus:
	 *
	 * 1. Make sure NH exists
	 * 2. Call backend
	 * 3. Backend validates existing/active username
	 *
	 * Expected response:
	 *
	 * {
	 *   success: true,
	 *   exists: true,
	 *   message: "Active user found"
	 * }
	 */
	const validateUsername = async (
		_,
		value
	) => {
		if (!value || !value.trim()) {
			return Promise.resolve();
		}

		const nhName =
			form.getFieldValue('nhName');

		if (!nhName) {
			return Promise.reject(
				new Error(
					'Please select Nursing Home Name first.'
				)
			);
		}

		try {
			const result =
				await checkExistingUsernameApi(
					value.trim(),
					nhName
				);

			if (result?.exists === true) {
				return Promise.resolve();
			}

			return Promise.reject(
				new Error(
					'No active user found'
				)
			);
		} catch (err) {
			console.error(
				'Username validation error:',
				err
			);

			const errorMessage =
				err?.response?.data?.message ||
				err?.response?.data ||
				err?.message ||
				'No active user found';

			return Promise.reject(
				new Error(errorMessage)
			);
		}
	};

	/*
	 * =========================================================
	 * SUBMIT NRIC MODIFICATION REQUEST
	 * =========================================================
	 */
	const onFinish = async (values) => {
		setSubmit({
			loading: true,
			success: null,
			error: null,
		});

		const payload = {
			nhName:
				values.nhName?.trim(),

			username:
				values.userName?.trim(),

			fullName:
				values.fullName?.trim(),

			oldNric:
				values.oldNric
					?.trim()
					?.toUpperCase(),
            completionEmailList: 
                values.completionEmailList
                    .split(',')
                    .map((email) => email.trim())
                    .join(','),
			newNric:
				values.newNric
					?.trim()
					?.toUpperCase(),

			createdBy:
				userDetails?.userId,

			status: 'P',
		};

		try {
			await submitModifyNricApi(
				payload
			);

			setSubmit({
				loading: false,

				success:
					'NRIC modification request submitted successfully.',

				error: null,
			});

			message.success(
				'NRIC modification request submitted successfully!'
			);

			/*
			 * Reset form.
			 *
			 * For normal users, initialValues will
			 * restore their NH automatically.
			 */
			form.resetFields();

		} catch (err) {
			console.error(
				'Modify NRIC Error:',
				err
			);

			const errorMessage =
				err?.response?.data?.message ||
				err?.response?.data ||
				err?.message ||
				'Failed to submit NRIC modification request.';

			setSubmit({
				loading: false,
				success: null,
				error:
					errorMessage,
			});

			message.error(
				errorMessage
			);
		}
	};

	return (
		<div
			className={
				styles['container']
			}
		>
			<div
				className={
					styles['wrapper']
				}
			>
				<div
					className={
						styles[
							'intro-para'
						]
					}
				>
					<div
						className={
							styles[
								'header-block'
							]
						}
					>
						<h1
							className={
								styles[
									'page-title'
								]
							}
						>
							Modify{' '}
							<span>
								NRIC
							</span>
						</h1>

						<p
							className={
								styles[
									'subtitle'
								]
							}
						>
							Submit a request
							to update an
							existing user's
							NRIC / FIN
						</p>
					</div>

					<p
						className={
							styles[
								'head-text'
							]
						}
					>
						Dear{' '}
						{loggedInFullName ??
							userDetails?.userId}
						,
					</p>

					<p
						className={
							styles[
								'head-text'
							]
						}
					>
						Please provide the
						below details to
						submit an NRIC
						modification
						request.
					</p>
				</div>

				<Form
					id="modify-nric-form"
					name="modify-nric-form"
					form={form}
					labelCol={{
						span: 24,
					}}
					initialValues={
						initialValues
					}
					onFinish={
						onFinish
					}
					validateTrigger={[
						'onBlur',
						'onChange',
					]}
					disabled={
						submit.loading ||
						SSPFacilities
							?.error
							?.length > 0
					}
				>
					<Row gutter={20}>

						{/* =========================
						    NURSING HOME
						   ========================= */}
						<Col xs={24} md={12} xxl={8}>
                            <Form.Item
                                label="Nursing Home Name"
                                name="nhName"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select Nursing Home Name!',
                                    },
                                ]}
                            >
                                {isAdmin ? (
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Please select Nursing Home Name"
                                        suffixIcon={
                                            <IoIosArrowDown
                                                style={{ fontSize: '14px' }}
                                            />
                                        }
                                        options={allNhNames.map((item) => ({
                                            label: item,
                                            value: item,
                                        }))}
                                        onChange={handleNhChange}
                                    />
                                ) : (
                                    <Input
                                        placeholder="Nursing Home Name"
                                        disabled
                                    />
                                )}
                            </Form.Item>
                        </Col>


						{/* =========================
						    USERNAME
						   ========================= */}
						{/* Username */}
                        <Col xs={24} md={12} xxl={8}>
                            <Form.Item
                                label="Username"
                                name="userName"
                                validateTrigger="onBlur"
                                rules={[
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: 'Please input Username!',
                                    },
                                    {
                                        validator: validateUsername,
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Please input existing ECCareSuite User ID"
                                    autoComplete="off"
                                />
                            </Form.Item>
                        </Col>


						{/* =========================
						    FULL NAME
						   ========================= */}
						<Col
							xs={24}
							md={12}
							xxl={8}
						>
							<Form.Item
								label="Full Name"
								name="fullName"
								rules={[
									{
										required:
											true,

										whitespace:
											true,

										message:
											'Please input Full Name!',
									},
								]}
							>
								<Input
									placeholder="Please input Full Name"
								/>
							</Form.Item>
						</Col>


						{/* =========================
						    OLD NRIC
						   ========================= */}
						<Col
							xs={24}
							md={12}
							xxl={8}
						>
							<Form.Item
								label="Old NRIC / FIN"
								name="oldNric"
								normalize={(
									value
								) =>
									value
										? value
												.toUpperCase()
												.trim()
										: ''
								}
								rules={[
									{
										required:
											true,

										whitespace:
											true,

										message:
											'Please input Old NRIC / FIN!',
									},

									{
										validator: (
											_,
											value
										) => {

											if (
												!value
											) {
												return Promise.resolve();
											}

											const oldNric =
												value
													.trim()
													.toUpperCase();

											if (
												!isValidNric(
													oldNric
												)
											) {
												return Promise.reject(
													new Error(
														'Invalid Old NRIC / FIN!'
													)
												);
											}

											return Promise.resolve();
										},
									},
								]}
							>
								<Input
									placeholder="Please input Old NRIC / FIN"
									maxLength={
										9
									}
								/>
							</Form.Item>
						</Col>


						{/* =========================
						    NEW NRIC
						   ========================= */}
						<Col
							xs={24}
							md={12}
							xxl={8}
						>
							<Form.Item
								label="New NRIC / FIN"
								name="newNric"

								/*
								 * Revalidate New NRIC
								 * whenever Old NRIC changes.
								 */
								dependencies={[
									'oldNric',
								]}

								normalize={(
									value
								) =>
									value
										? value
												.toUpperCase()
												.trim()
										: ''
								}

								rules={[
									{
										required:
											true,

										whitespace:
											true,

										message:
											'Please input New NRIC / FIN!',
									},

									{
										validator: (
											_,
											value
										) => {

											if (
												!value
											) {
												return Promise.resolve();
											}

											const newNric =
												value
													.trim()
													.toUpperCase();

											const oldNric =
												form
													.getFieldValue(
														'oldNric'
													)
													?.trim()
													?.toUpperCase();

											if (
												!isValidNric(
													newNric
												)
											) {
												return Promise.reject(
													new Error(
														'Invalid New NRIC / FIN!'
													)
												);
											}

											if (
												oldNric &&
												newNric ===
													oldNric
											) {
												return Promise.reject(
													new Error(
														'New NRIC / FIN cannot be the same as Old NRIC / FIN.'
													)
												);
											}

											return Promise.resolve();
										},
									},
								]}
							>
								<Input
									placeholder="Please input New NRIC / FIN"
									maxLength={
										9
									}
								/>
							</Form.Item>
						</Col>
                        {/* Completion Email List */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
                                label="Confirmation Email List"
                                name="completionEmailList"
                                tooltip="Enter the email addresses that should receive the confirmation notification. Separate multiple emails with commas (,)."
                                rules={[
                                    {
                                        required: true,
                                        message: 'At least one email is required.',
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value?.trim()) {
                                                return Promise.resolve();
                                            }

                                            const emails = value
                                                .split(',')
                                                .map((email) => email.trim());

                                            // Detect empty entries caused by leading/trailing/double commas
                                            if (emails.some((email) => !email)) {
                                                return Promise.reject(
                                                    new Error('Please remove empty email entries.')
                                                );
                                            }

                                            const emailRegex =
                                                /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                                            const invalidEmails = emails.filter(
                                                (email) => !emailRegex.test(email)
                                            );

                                            if (invalidEmails.length > 0) {
                                                return Promise.reject(
                                                    new Error(
                                                        `Invalid emails: ${invalidEmails.join(', ')}`
                                                    )
                                                );
                                            }

                                            // Case-insensitive duplicate check
                                            const normalizedEmails = emails.map(
                                                (email) => email.toLowerCase()
                                            );

                                            const duplicateEmails = normalizedEmails.filter(
                                                (email, index) =>
                                                    normalizedEmails.indexOf(email) !== index
                                            );

                                            if (duplicateEmails.length > 0) {
                                                return Promise.reject(
                                                    new Error(
                                                        `Duplicate emails: ${[
                                                            ...new Set(duplicateEmails),
                                                        ].join(', ')}`
                                                    )
                                                );
                                            }

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea
                                    autoSize={{
                                        minRows: 3,
                                        maxRows: 5,
                                    }}
                                    placeholder="Please input Confirmation Email List"
                                />
                            </Form.Item>
						</Col>

						{/* =========================
						    SUBMIT
						   ========================= */}
						<Col xs={24}>
							<Form.Item
								style={{
									marginBottom:
										0,
								}}
							>
								<Button
									className="premium-btn"

									loading={
										submit.loading
									}

									type="primary"

									htmlType="submit"

									block
								>
									Submit NRIC
									Modification
									Request
								</Button>
							</Form.Item>
						</Col>

					</Row>
				</Form>
			</div>
		</div>
	);
};

export default ModifyNric;